import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Subscription, UsageStats, FeatureAccess } from '../types/subscription';
import apiService from '../services/api';
import { useAuth } from './AuthContext';

interface SubscriptionContextType {
  subscription: Subscription | null;
  usageStats: UsageStats | null;
  featureAccess: FeatureAccess;
  isLoading: boolean;
  refreshSubscription: () => Promise<void>;
  hasFeature: (feature: keyof FeatureAccess) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine feature access based on subscription
  const featureAccess: FeatureAccess = React.useMemo(() => {
    if (!subscription) {
      return {
        hasInvoicing: false,
        hasReports: false,
        hasBulkOperations: false,
        hasApiAccess: false,
        hasVendorCompliance: false,
      };
    }

    // Map plan IDs to features
    const planFeatures: Record<string, FeatureAccess> = {
      starter: {
        hasInvoicing: false,
        hasReports: false,
        hasBulkOperations: false,
        hasApiAccess: false,
        hasVendorCompliance: false,
      },
      professional: {
        hasInvoicing: true,
        hasReports: true,
        hasBulkOperations: true,
        hasApiAccess: false,
        hasVendorCompliance: false,
      },
      enterprise: {
        hasInvoicing: true,
        hasReports: true,
        hasBulkOperations: true,
        hasApiAccess: true,
        hasVendorCompliance: true,
      },
    };

    return planFeatures[subscription.planId] || planFeatures.starter;
  }, [subscription]);

  const refreshSubscription = async () => {
    if (!isAuthenticated || authLoading) {
      setSubscription(null);
      setUsageStats(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const [subData, usageData] = await Promise.all([
        apiService.getCurrentSubscription(),
        apiService.getUsageStats().catch(() => null),
      ]);

      setSubscription(subData);
      setUsageStats(usageData);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
      setUsageStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  const hasFeature = (feature: keyof FeatureAccess): boolean => {
    return featureAccess[feature];
  };

  // Fetch subscription when user logs in
  useEffect(() => {
    refreshSubscription();
  }, [isAuthenticated, authLoading]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        usageStats,
        featureAccess,
        isLoading,
        refreshSubscription,
        hasFeature,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
